import React from 'react';
import {Link} from 'react-router-dom';
import {PlayerBrowseItem} from '@/features/players/api';
import {availabilityLabel, PlayerWatchButton, ratingLabel, reportDateLabel, reportLabel, sourceLabel, SquadLifecycleBadge, SquadLifecycleStatus} from './PlayerBrowseTable';

interface PlayerBrowseCardProps {
  player: PlayerBrowseItem;
  detailHref: string;
  squadStatus?: SquadLifecycleStatus;
  canManageWatchlist?: boolean;
  watchPending?: ReadonlySet<string>;
  onWatchChange?: (player: PlayerBrowseItem, next: boolean) => void;
}

const PlayerBrowseCard: React.FC<PlayerBrowseCardProps> = ({player, detailHref, squadStatus, canManageWatchlist, watchPending, onWatchChange}) => (
  <article aria-label={player.name} className="min-w-0 rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
    <div className="flex min-w-0 items-start justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        {player.photoUrl && <img className="h-12 w-12 shrink-0 rounded-full object-cover" src={player.photoUrl} alt={player.name} />}
        <div className="min-w-0">
          <h2 className="break-words text-lg font-bold">
            <Link className="rounded-md text-club-blue-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:text-club-blue-300" to={detailHref}>
              {player.name}
            </Link>
          </h2>
          <p className="break-words text-sm text-gray-600 dark:text-gray-400">{player.position} / {player.team ?? 'No team'}</p>
        </div>
      </div>
      <span className="flex shrink-0 flex-col items-end gap-2">
        {canManageWatchlist && onWatchChange && player.scope === 'external' && (
          <PlayerWatchButton player={player} disabled={watchPending?.has(player.id)} onChange={onWatchChange} />
        )}
        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200">Age {player.age}</span>
        <SquadLifecycleBadge status={squadStatus} />
      </span>
    </div>
    <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
      <div><dt className="text-gray-500 dark:text-gray-400">Availability</dt>{' '}<dd className="font-semibold text-gray-900 dark:text-gray-100">{availabilityLabel(player.availability)}</dd></div>
      <div><dt className="text-gray-500 dark:text-gray-400">Latest rating</dt>{' '}<dd className="font-semibold text-gray-900 dark:text-gray-100">{ratingLabel(player)}</dd></div>
      <div><dt className="text-gray-500 dark:text-gray-400">Rating source</dt>{' '}<dd className="font-semibold text-gray-900 dark:text-gray-100">{sourceLabel(player.ratingSource)}</dd></div>
      <div><dt className="text-gray-500 dark:text-gray-400">Last report</dt>{' '}<dd className="font-semibold text-gray-900 dark:text-gray-100"><time dateTime={player.lastReportAt ?? undefined}>{reportDateLabel(player.lastReportAt)}</time></dd></div>
      <div><dt className="text-gray-500 dark:text-gray-400">Reports</dt>{' '}<dd className="font-semibold text-gray-900 dark:text-gray-100">{reportLabel(player.reportCount)}</dd></div>
    </dl>
  </article>
);

export default PlayerBrowseCard;
